import PageHeader from "../../components/ui/PageHeader";
import { useTranslation } from "react-i18next";
import { StyledTableCell, StyledTableRow, TableWrapper } from "../../components/ui/table";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  styled,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import { useAppDispatch, useAppSelector } from "../../redux";
import { AppDispatch } from "../../redux/store";
import { getBreefInviteUserDetails, resendBreefInvite } from "../../service/BreefInviteService";
import { BreefInviteUserDetail } from "../../redux/slices/BreefInviteSlice";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import ConsentAnswers from "./ConsentAnswers";
import i18next from "i18next";




const statusColor = (status: BreefInviteUserDetail["sendStatus"]) => {
  if (status === "SENT") return "success";
  if (status === "FAILED") return "error";
  return "warning";
};

export default function BreefTrackingDetail() {
  const [t] = useTranslation("global");
  const { breefId } = useParams();
  const isMobile = useMediaQuery("(max-width:600px)");
  const dispatch: AppDispatch = useAppDispatch();
  const data = useAppSelector((state) => state.breefInvites);
  const navigate = useNavigate();
  const [resendingId, setResendingId] = useState<string | null>(null);

  const fetchDetails = async () => {
    if (breefId) {
      await getBreefInviteUserDetails(dispatch, isMobile, navigate, breefId);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breefId]);

  const handleResend = async (inviteId: string) => {
    setResendingId(inviteId);
    try {
      const result = await resendBreefInvite(inviteId, isMobile, navigate);
      if (result?.success) {
        showSuccessToast(i18next.t("messages.inviteResent"), isMobile);
      } else {
        showErrorToast(result?.error || "Resend failed", isMobile);
      }
      await fetchDetails();
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Layout>
      <PageHeader title={t("tracking.detailTitle")} />
      <TableWrapper component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell align="center">
                User
              </StyledTableCell>
              <StyledTableCell align="center">
                Destination
              </StyledTableCell>
              <StyledTableCell align="center">
                Medium
              </StyledTableCell>
              <StyledTableCell align="center">
                Status
              </StyledTableCell>
              <StyledTableCell align="center">
                {t("queues.delivery")}
              </StyledTableCell>
              <StyledTableCell align="center">
                Attempts
              </StyledTableCell>
              <StyledTableCell align="center">
                Last Attempt
              </StyledTableCell>
              <StyledTableCell align="center">
                Error
              </StyledTableCell>
              <StyledTableCell align="center">
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.userDetailsData.results.map((invite) => (
              <StyledTableRow key={invite.inviteId}>
                <TableCell align="center">{invite.userName}</TableCell>
                <TableCell align="center">{invite.destination}</TableCell>
                <TableCell align="center">
                  <Chip label={invite.inviteType.toUpperCase()} size="small" />
                </TableCell>
                <TableCell align="center">
                  {invite.sendStatus ? (
                    <Chip label={t(`queues.inviteStatuses.${invite.sendStatus}`, { defaultValue: invite.sendStatus })} color={statusColor(invite.sendStatus)} size="small" />
                  ) : (
                    <Tooltip title="This invite was sent before delivery tracking existed - no send outcome was recorded for it">
                      <Chip label={t("fields.notTracked")} size="small" variant="outlined" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {invite.delivery?.status ? (
                    <Tooltip title={invite.delivery.detail || ""}>
                      <Chip label={t(`queues.deliveryStatuses.${invite.delivery.status}`, { defaultValue: invite.delivery.status })} color={invite.delivery.status === "delivered" ? "success" : ["failed", "expired"].includes(invite.delivery.status) ? "error" : "default"} size="small" variant={invite.delivery.status === "pending" ? "outlined" : "filled"} />
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="center">{invite.attempts !== undefined ? `${invite.attempts}/3` : "-"}</TableCell>
                <TableCell align="center">
                  {invite.lastAttemptAt ? new Date(invite.lastAttemptAt).toLocaleString("da-DK") : "-"}
                </TableCell>
                <TableCell align="center">
                  {invite.sendError ? (
                    <Tooltip title={invite.sendError}>
                      <Box
                        sx={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {invite.sendError}
                      </Box>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="center">
                  {invite.sendStatus === "FAILED" && (invite.attempts ?? 0) < 3 && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={resendingId === invite.inviteId}
                      onClick={() => handleResend(invite.inviteId)}
                    >
                      {resendingId === invite.inviteId ? "Resending..." : "Resend"}
                    </Button>
                  )}
                </TableCell>
              </StyledTableRow>
            ))}
            {data.userDetailsData.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No invites found for this breef
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
      {breefId && <ConsentAnswers breefId={breefId} />}
    </Layout>
  );
}
